import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Foot from '../../components/Common/Footer/Footer';
import api, { getStorageUrl } from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Calendar,
  User,
  Building2,
  FileText,
  Clock,
  Printer,
  ChevronRight,
  Loader2,
  ShieldCheck,
  ClipboardList,
  Mail,
  MoreVertical,
  Activity,
  Download,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import styles from './ViewInspection.module.css';
import NotFound from '../Error/NotFound';


const ViewInspection = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [inspection, setInspection] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchInspectionDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/inspections/${id}`);
      if (response.data.success) {
        setInspection(response.data.data);
      } else {
        toast.error('Failed to load ');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Backend connection lost');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInspectionDetails();
  }, [fetchInspectionDetails]);

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return { label: 'APPROVED', style: styles.approved, icon: <CheckCircle2 size={14} /> };
      case 'rejected':
        return { label: 'REJECTED', style: styles.rejected, icon: <XCircle size={14} /> };
      default:
        return { label: 'PENDING', style: styles.pending, icon: <AlertCircle size={14} /> };
    }
  };

  const calculateDaysDelta = (date1, date2) => {
    if (!date1 || !date2) return '---';
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className={styles['view-inspection-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={48} className={styles.spinner} />
            <p>Syncing technical inspection records...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!inspection) {
    return <NotFound />;
  }


  const statusConfig = getStatusConfig(inspection.clearance_status);

  return (
    <div className={`${styles['view-inspection-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[{ label: 'Inspections', path: '/inspections' }, { label: `#INS-${id}` }]} />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Inspection Details</h1>
              <p>Review technical reports, itemized audit, and clearance status</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={16} />
                <span>Print Memo</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`/inspections/edit/${id}`)}>
                <Clock size={16} />
                <span>Reschedule</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['inspection-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>Inspection #INS-{id}</h1>
                <span className={`${styles['status-badge']} ${statusConfig.style}`}>
                  {statusConfig.icon}
                  {statusConfig.label}
                </span>
              </div>
            </div>

            <div className={styles['inspection-meta']}>
              <div className={styles['meta-item']}>
                <FileText size={16} />
                <span>PO: <strong>{inspection.purchase_order?.po_number || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Building2 size={16} />
                <span>Vendor: <strong>{inspection.vendor?.vendor_name || '---'}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>Notified: <strong>{new Date(inspection.notified_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-age']}`}><Calendar size={20} /></div>
                <span className={styles['m-trend']}>Notification Date</span>
              </div>
              <div className={styles['m-info']}>
                <label>Notification Age</label>
                <h2>{calculateDaysDelta(inspection.notified_date, new Date())} Days</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-speed']}`}><Activity size={20} /></div>
                <span className={styles['m-trend']}>Efficiency</span>
              </div>
              <div className={styles['m-info']}>
                <label>Clearance Delta</label>
                <h2>{calculateDaysDelta(inspection.notified_date, inspection.actual_inspection_date)} Days</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-agency']}`}><ShieldCheck size={20} /></div>
                <span className={styles['m-trend']}>Assurance Agency</span>
              </div>
              <div className={styles['m-info']}>
                <label>Testing Body</label>
                <h2>{inspection.agency_type}</h2>
              </div>
            </div>

            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-status']}`}><ClipboardList size={20} /></div>
                <span className={styles['m-trend']}>Items Count</span>
              </div>
              <div className={styles['m-info']}>
                <label>Equipment Count</label>
                <h2>{inspection.purchase_order?.items?.length || 0} Units</h2>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className={styles['dashboard-grid']}>
            {/* Left Column (70%) */}
            <div className={styles['left-column']}>
              {/* Detailed Breakdown Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <FileText size={18} />
                    <h3>Inspection Breakdown</h3>
                  </div>
                  <span className={styles['h-badge']}>Detailed Specs</span>
                </div>

                <div className={styles['two-col-row']}>
                  <div className={styles['info-group']}>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Project Context</label>
                    <p style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>{inspection.project?.project_name || '---'}</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Code: {inspection.project?.project_code || '---'}</p>
                  </div>
                  <div className={styles['info-group']}>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>Contact Person</label>
                    <p style={{ margin: 0, fontWeight: 700, color: '#1e293b' }}>{inspection.contact_person_name || '---'}</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Designated Vendor Rep</p>
                  </div>
                </div>

                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f1f5f9' }}>
                  <label style={{ fontSize: '11px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>Observation Remarks</label>
                  <p className={styles['note-text']}>{inspection.remarks || 'No specific technical observations recorded for this session.'}</p>
                </div>
              </div>

              {/* Purchase Order Items Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <ClipboardList size={18} />
                    <h3>Linked PO Items</h3>
                  </div>
                  <span className={styles['h-badge']}>{inspection.purchase_order?.items?.length || 0} Line Items</span>
                </div>

                {!isMobile ? (
                  <div className={styles['table-scroller']}>
                    <table className={styles['data-table']}>
                      <thead>
                        <tr>
                          <th>ITEM NAME</th>
                          <th>UNIT</th>
                          <th className={styles['text-right']}>QTY</th>
                          <th className={styles['text-right']}>TOTAL AMOUNT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inspection.purchase_order?.items?.map((item, idx) => (
                          <tr key={idx}>
                            <td>
                              <div className={styles['item-info']}>
                                <span className={styles['i-name']}>{item.item_name}</span>
                                <span className={styles['i-ref']}>SKU: {item.work_order_item_id}</span>
                              </div>
                            </td>
                            <td>{item.unit || 'Nos'}</td>
                            <td className={`${styles.bold} ${styles['text-right']}`}>{item.quantity}</td>
                            <td className={`${styles.bold} ${styles['text-right']}`}>₹{parseFloat(item.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          </tr>
                        ))}
                        {(!inspection.purchase_order?.items || inspection.purchase_order.items.length === 0) && (
                          <tr>
                            <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No items associated with this Purchase Order.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles['item-cards']}>
                    {inspection.purchase_order?.items?.map((item, idx) => (
                      <div key={idx} className={styles['item-card-sub']}>
                        <div className={styles['item-card-header']}>
                          <span className={styles['item-card-name']}>{item.item_name}</span>
                          <span className={styles['item-card-qty']}>{item.quantity} {item.unit || 'Nos'}</span>
                        </div>
                        <div className={styles['item-card-footer']}>
                          <span className={styles['item-card-sku']}>SKU: {item.work_order_item_id}</span>
                          <span className={styles['item-card-amount']}>₹{parseFloat(item.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                    ))}
                    {(!inspection.purchase_order?.items || inspection.purchase_order.items.length === 0) && (
                      <div className={styles['empty-log-cell']}>No items associated with this Purchase Order.</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column (30%) */}
            <div className={styles['right-column']}>
              {/* Personnel Information Row */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <ShieldCheck size={18} />
                    <h3>Personnel</h3>
                  </div>
                </div>

                <div className={`${styles['st-item']} ${styles['st-blue']}`}>
                  <div className={`${styles['st-avatar']} ${styles['bg-blue']}`}>
                    {inspection.creator?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className={styles['st-info']}>
                    <label>Scheduled By</label>
                    <h4>{inspection.creator?.name || 'Technical User'}</h4>
                  </div>
                </div>

                {inspection.cleared_by && (
                  <div className={`${styles['st-item']} ${styles['st-green']}`}>
                    <div className={`${styles['st-avatar']} ${styles['bg-green']}`}>
                      {inspection.cleared_by?.name?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div className={styles['st-info']}>
                      <label>Cleared By</label>
                      <h4>{inspection.cleared_by?.name || 'System Auditor'}</h4>
                    </div>
                  </div>
                )}

                {!inspection.cleared_by && (
                  <div className={`${styles['st-item']} ${styles['st-orange']}`}>
                    <div className={`${styles['st-avatar']} ${styles['bg-orange']}`}>?</div>
                    <div className={styles['st-info']}>
                      <label>Clearance Status</label>
                      <h4 style={{ color: '#f97316' }}>Awaiting Approval</h4>
                    </div>
                  </div>
                )}
              </div>

              {/* Technical Documents Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Activity size={18} />
                    <h3>Technical Docs</h3>
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {inspection.purchase_order?.po_document_filepath ? (
                    <a
                      href={getStorageUrl(inspection.purchase_order.po_document_filepath)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles['doc-box']}
                    >
                      <div className={styles['doc-icon']}><Download size={18} /></div>
                      <div className={styles['doc-info']}>
                        <span>PO Document</span>
                        <small>Approved File</small>
                      </div>
                    </a>
                  ) : (
                    <div className={styles['doc-box']} style={{ opacity: 0.6, cursor: 'not-allowed' }}>
                      <div className={styles['doc-icon']}><FileText size={18} /></div>
                      <div className={styles['doc-info']}>
                        <span>PO Document</span>
                        <small>Not Available</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Inspection Timeline Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}>
                    <Clock size={18} />
                    <h3>Audit Timeline</h3>
                  </div>
                </div>

                <div className={styles['timeline-v']}>
                  <div className={styles['tl-item']}>
                    <div className={styles['tl-dot']} style={{ borderColor: '#f97316' }}></div>
                    <div className={styles['tl-content']}>
                      <h4>Notified for Inspection</h4>
                      <p>{new Date(inspection.notified_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <div className={styles['tl-item']}>
                    <div className={styles['tl-dot']} style={{ borderColor: inspection.actual_inspection_date ? '#10b981' : '#f1f5f9' }}></div>
                    <div className={styles['tl-content']}>
                      <h4>Technical Inspection Conducted</h4>
                      <p>{inspection.actual_inspection_date ? new Date(inspection.actual_inspection_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Pending session'}</p>
                    </div>
                  </div>
                  <div className={styles['tl-item']}>
                    <div className={styles['tl-dot']} style={{ borderColor: inspection.clearance_status !== 'pending' ? '#10b981' : '#f1f5f9' }}></div>
                    <div className={styles['tl-content']}>
                      <h4>Clearance Verification</h4>
                      <p>{inspection.clearance_status !== 'pending' ? 'Decision Finalized' : 'Awaiting quality report'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Foot />
        </main>
      </div>
    </div>
  );
};

export default ViewInspection;
